import React, { useCallback, useMemo } from 'react';
import { Trans } from '@lingui/macro';
import { AlarmCategory_POST } from 'util/backendapi/types/Model';
import { patchApi, postApi } from 'util/backendapi/fetch';
import { showErrorsInFormik } from 'util/backendapi/error-formik';
import { getExpectedFields } from 'util/backendapi/error';
import { useIsMounted } from 'util/hooks';
import { FormikHelpers, FormikErrors, Formik, Form, Field } from 'formik';
import { FormSection, FormItem } from 'components/base/form/FormItem';
import ErrorNotice, {
  FieldError,
} from 'components/base/form/errornotice/errornotice';
import { ButtonPrimary } from 'components/base/button/button';
import ModalContent from 'components/base/modal/modalcontent';
import ActionBlock from 'components/base/actionblock/actionblock';
import ButtonHideModal from 'components/base/modal/buttonhidemodal';
import { useGetApi } from 'hooks/use-get-api';
import Loading from 'components/base/loading/loading';
import { Enum } from 'util/backendapi/models/api.interfaces';

type FormValues = Omit<AlarmCategory_POST, 'status'> & { id: number };

interface Props {
  alarmCategoryId?: number;
  onAfterSubmit: () => void;
}

export function EditAlarmCategoryModal({
  alarmCategoryId,
  onAfterSubmit,
}: Props) {
  const isMounted = useIsMounted();

  const validate = useCallback(
    (values: FormValues): FormikErrors<FormValues> => {
      const errors: FormikErrors<FormValues> = {};

      if (!values.name) {
        errors.name = (<Trans>Name is required.</Trans>) as any;
      }

      return errors;
    },
    []
  );

  const handleSubmit = useCallback(
    async (values: FormValues, formik: FormikHelpers<FormValues>) => {
      try {
        if (alarmCategoryId) {
          await patchApi(`/alarm-categories/${alarmCategoryId}/`, values);
        } else {
          await postApi(`/alarm-categories/`, {
            ...values,
            status: Enum.AlarmCategory_STATUS.active,
          });
        }
        onAfterSubmit();
      } catch (err) {
        if (isMounted()) {
          formik.setSubmitting(false);
          showErrorsInFormik(
            formik,
            err,
            getExpectedFields(values),
            ErrorNotice
          );
        }
      }
    },
    [alarmCategoryId, onAfterSubmit, isMounted]
  );

  const [alarmCategoryDetail] = useGetApi(
    alarmCategoryId ? `/alarm-categories/${alarmCategoryId}/` : null
  );

  const initialValues: FormValues | null = useMemo((): FormValues | null => {
    if (alarmCategoryId && !alarmCategoryDetail.data) {
      return null;
    }

    if (alarmCategoryId && alarmCategoryDetail.data) {
      return {
        id: alarmCategoryId,
        name: alarmCategoryDetail.data.name,
      };
    }

    return {
      id: 0,
      name: '',
    };
  }, [alarmCategoryId, alarmCategoryDetail.data]);

  return (
    <ModalContent
      header={
        alarmCategoryId ? (
          <Trans>Edit alarm category</Trans>
        ) : (
          <Trans>Create alarm category</Trans>
        )
      }
    >
      {alarmCategoryDetail.isLoading ? <Loading /> : null}
      {initialValues ? (
        <Formik<FormValues>
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validate={validate}
        >
          {(formik) => (
            <Form>
              <FormSection>
                <FormItem
                  label={<Trans>Name</Trans>}
                  fieldId="alarm-category-name"
                >
                  <Field name="name" id="alarm-category-name" autoFocus />
                  <FieldError name="name" />
                </FormItem>
              </FormSection>
              {formik.status}
              <ActionBlock>
                <ButtonHideModal />
                <ButtonPrimary
                  data-testid="alarm-category-submit"
                  type="submit"
                  disabled={formik.isSubmitting}
                  iconType="icon-save"
                >
                  {alarmCategoryId ? (
                    <Trans>Save</Trans>
                  ) : (
                    <Trans>Create</Trans>
                  )}
                </ButtonPrimary>
              </ActionBlock>
            </Form>
          )}
        </Formik>
      ) : null}
    </ModalContent>
  );
}
